import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Info } from 'lucide-react';

interface LinkedInReauthorizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReauthorize: () => void;
  isInitialSetup?: boolean;
}

export function LinkedInReauthorizeModal({
  isOpen,
  onClose,
  onReauthorize,
  isInitialSetup = false
}: LinkedInReauthorizeModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {isInitialSetup ? 'Connect LinkedIn Account' : 'LinkedIn Re-authorization Required'}
          </DialogTitle>
          <DialogDescription className="pt-4">
            {isInitialSetup ? (
              <div className="space-y-4">
                <p>To use LinkPilot, you need to connect your LinkedIn account. This allows us to post content on your behalf.</p>
                <div className="flex items-start gap-2 rounded-lg bg-muted p-4">
                  <Info className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">
                      Need help getting started? Check out our{' '}
                      <a 
                        href="https://intercom.help/linkpilot-app/en/articles/10346177-welcome-to-linkpilot"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        setup guide
                      </a>
                      {' '}for detailed instructions.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p>Your LinkedIn connection has expired or is about to expire. Please re-authorize to continue using LinkedIn features.</p>
                <div className="flex items-start gap-2 rounded-lg bg-muted p-4">
                  <Info className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    This is a security requirement from LinkedIn and helps ensure your account remains secure.
                  </p>
                </div>
              </div>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end space-x-2 mt-6">
          <Button variant="outline" onClick={onClose}>
            Later
          </Button>
          <Button 
            onClick={onReauthorize}
            className="bg-primary/10 hover:bg-primary/20 text-primary"
          >
            {isInitialSetup ? 'Connect LinkedIn' : 'Re-authorize LinkedIn'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}